import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  structuredData?: object | object[];
  noindex?: boolean;
}

const SEOHead = ({
  title = "FM & Public Sector Staffing Agency | PAMP Recruitment | Nationwide",
  description = "UK-wide facilities management and public sector staffing agency supplying DBS-cleared, PAYE workers to government, justice, education and housing clients.",
  keywords = "FM staffing agency UK, facilities management recruitment, public sector staffing, DBS cleared workers, PAYE staffing agency, government cleaning contracts, MOJ staffing, probation service cleaning, school cleaning staff, housing support workers, West Midlands recruitment, nationwide FM agency, MSP supplier UK, PSL recruitment agency",
  canonical,
  ogType = 'website',
  ogImage = "https://pamprecruitment.co.uk/og-image.jpg",
  structuredData,
  noindex = false,
}: SEOHeadProps) => {
  // Build a brand-suffixed title, then enforce a 60-char cap (preferred SEO length).
  const baseTitle = title.includes('PAMP Recruitment') ? title : `${title} | PAMP Recruitment`;
  const fullTitle = baseTitle.length > 60
    ? (title.length <= 60 ? title : title.slice(0, 57).trimEnd() + '…')
    : baseTitle;
  const url = canonical || `https://pamprecruitment.co.uk${window.location.pathname}`;
  // Keep descriptions within the 160-character limit search engines and social cards display.
  const shortDescription =
    description.length > 160 ? description.slice(0, 157).trimEnd() + '…' : description;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={shortDescription} />
      <meta name="keywords" content={keywords} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={shortDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:site_name" content="PAMP Recruitment" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={shortDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(structuredData) ? structuredData : [structuredData])}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
